import Component from '@glimmer/component';
import { t } from 'ember-intl';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Drawer from 'tio-ui/components/drawer';
import { formatCurrency, formatCycleDateLong, formatNumericDate } from 'tio-ui/format';
import { eq } from 'tio-ui/utilities';
import {
  PhCheckCircle,
  PhArrowCounterClockwise,
  PhWarningCircle,
  PhHourglass,
  PhClock,
} from 'ember-phosphor-icons';
import type { ContributionPaymentRecord } from 'tio-employee/services/reimagined-data';

/**
 * PaymentHistoryDrawer — Slide-out panel showing per-year payment history
 *
 * Opens from the right when a yearly contribution card is clicked.
 * Lists individual payments grouped by month with status icons matching
 * the Figma design: Sent (green check), Resent (blue arrow), Failed (red warning),
 * In Progress (hourglass), Pending (clock).
 *
 * @example
 * ```gts
 * <PaymentHistoryDrawer
 *   @isOpen={{this.isPaymentHistoryDrawerOpen}}
 *   @onClose={{this.closePaymentHistoryDrawer}}
 *   @year="2025"
 *   @payments={{this.paymentsForSelectedYear}}
 *   @total={{1500}}
 * />
 * ```
 */

type PaymentStatus = ContributionPaymentRecord['status'];

interface PaymentHistoryDrawerSignature {
  Args: {
    /** Whether the drawer is open */
    isOpen: boolean;
    /** Called when the drawer should close */
    onClose: () => void;
    /** Selected year label */
    year: string;
    /** Payments for the selected year */
    payments: ContributionPaymentRecord[];
    /** Total contribution amount for the year */
    total: number;
  };
  Element: HTMLDivElement;
}

function statusLabelKey(status: PaymentStatus): string {
  switch (status) {
    case 'sent':
      return 'slr.reimagined.status_sent';
    case 'resent':
      return 'slr.reimagined.status_resent';
    case 'failed':
      return 'slr.reimagined.status_failed';
    case 'in_progress':
      return 'slr.reimagined.status_in_progress';
    case 'pending':
      return 'slr.reimagined.status_pending';
  }
}

/** Background color for the status chip */
function statusChipBg(status: PaymentStatus): string {
  switch (status) {
    case 'sent':
      return 'tio-bg-info-soft tio-fg-info-h';
    case 'resent':
      return 'tio-bg-info-soft tio-fg-info-h';
    case 'failed':
      return 'tio-bg-danger-soft tio-fg-danger-h';
    case 'in_progress':
      return 'tio-bg-info-soft tio-fg-info-h';
    case 'pending':
      return 'tio-bg-sunken tio-fg-muted';
  }
}

/** Background color for the circular icon container */
function statusIconBg(status: PaymentStatus): string {
  switch (status) {
    case 'sent':
      return 'tio-bg-info-soft';
    case 'resent':
      return 'tio-bg-info-soft';
    case 'failed':
      return 'tio-bg-danger-soft';
    case 'in_progress':
      return 'tio-bg-info-soft';
    case 'pending':
      return 'tio-bg-sunken';
  }
}

/** Icon color for the status icon */
function statusIconColor(status: PaymentStatus): string {
  switch (status) {
    case 'sent':
      return 'tio-fg-info';
    case 'resent':
      return 'tio-fg-info-h';
    case 'failed':
      return 'tio-fg-danger-h';
    case 'in_progress':
      return 'tio-fg-info';
    case 'pending':
      return 'tio-fg-disabled';
  }
}

/** Date action label — "paid" for sent/resent, "failed" for failed */
function dateActionLabelKey(status: PaymentStatus): string {
  switch (status) {
    case 'failed':
      return 'slr.reimagined.date_action_failed';
    default:
      return 'slr.reimagined.date_action_paid';
  }
}

/** Count of successful payments (sent + resent) */
function successfulCount(payments: ContributionPaymentRecord[]): number {
  return payments.filter((p) => p.status === 'sent' || p.status === 'resent').length;
}

/** Format amount always showing cents (e.g. "$125" or "$0.00") */
function formatPaymentAmount(amount: number | undefined | null): string {
  const value = amount ?? 0;
  if (value === 0) {
    return formatCurrency(0, 2);
  }
  return formatCurrency(value);
}

export default class PaymentHistoryDrawer extends Component<PaymentHistoryDrawerSignature> {
  get successfulPaymentCount() {
    return successfulCount(this.args.payments);
  }

  <template>
    <Drawer @isOpen={{@isOpen}} @onClose={{@onClose}} @size="lg" @placement="right" ...attributes>
      <:header>
        <div class="tio-col-tight">
          <Heading @level={{3}} class="tio-fg-primary">
            {{t "slr.reimagined.contribution_history_year" year=@year}}
          </Heading>
          <Text @size="sm" @weight="medium" class="tio-fg-secondary leading-relaxed">
            {{t "slr.reimagined.successful_payments" count=this.successfulPaymentCount}}
          </Text>
        </div>
      </:header>
      <:body>
        <div class="tio-col py-2">
          {{#each @payments as |payment|}}
            <div class="tio-bg-surface border tio-border-default rounded-2xl p-4 tio-row-between">
              {{! Left: Status icon + payment details }}
              <div class="tio-row tio-fill-min">
                <div
                  class="w-8 h-8 rounded-full tio-row-center shrink-0
                    {{statusIconBg payment.status}}"
                >
                  {{#if (eq payment.status "sent")}}
                    <PhCheckCircle
                      @size={{24}}
                      @weight="fill"
                      class={{statusIconColor payment.status}}
                    />
                  {{else if (eq payment.status "resent")}}
                    <PhArrowCounterClockwise
                      @size={{20}}
                      @weight="bold"
                      class={{statusIconColor payment.status}}
                    />
                  {{else if (eq payment.status "failed")}}
                    <PhWarningCircle
                      @size={{24}}
                      @weight="fill"
                      class={{statusIconColor payment.status}}
                    />
                  {{else if (eq payment.status "in_progress")}}
                    <PhHourglass
                      @size={{20}}
                      @weight="fill"
                      class={{statusIconColor payment.status}}
                    />
                  {{else}}
                    <PhClock @size={{20}} @weight="fill" class={{statusIconColor payment.status}} />
                  {{/if}}
                </div>
                <div class="flex flex-col gap-1.5 min-w-0">
                  <Text @weight="medium" class="tio-fg-primary truncate">
                    {{formatCycleDateLong payment.benefitCycle}}
                  </Text>
                  <div class="flex items-center gap-1.5 text-sm tio-fg-secondary">
                    <span>{{payment.servicer}}</span>
                    {{#if payment.paidDate}}
                      <div class="w-1.5 h-1.5 rounded-full tio-bg-neutral"></div>
                      <span>{{t (dateActionLabelKey payment.status)}}
                        {{formatNumericDate payment.paidDate}}</span>
                    {{/if}}
                  </div>
                </div>
              </div>

              {{! Right: Amount + Status chip }}
              <div class="tio-stack-end gap-1 shrink-0">
                <Text @weight="semibold" class="tio-fg-secondary">
                  {{formatPaymentAmount payment.amount}}
                </Text>
                <span
                  class="text-[10px] font-semibold leading-relaxed px-2 py-0.5 rounded-lg
                    {{statusChipBg payment.status}}"
                >
                  {{t (statusLabelKey payment.status)}}
                </span>
              </div>
            </div>
          {{else}}
            <div class="py-10 tio-place-center gap-2 text-center">
              <Text @size="sm" class="tio-fg-secondary">{{t
                  "slr.reimagined.no_payments_for_year"
                  year=@year
                }}</Text>
            </div>
          {{/each}}
        </div>
      </:body>
    </Drawer>
  </template>
}
